import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import type { Track } from './types.js';

export type DeviceStats = {
  ready: boolean;
  bitrate: number | null;
  width: number | null;
  height: number | null;
  quality: number | null;
  packetLoss: number | null;
  framesDropped: number | null;
  framesPerSecond: number | null;
  codec: string | null | undefined;
};

export type RoomTrackStatsMediaType = 'video' | 'audio';

export type RoomTrackStatsIdentity = {
  roomId: Room['id'];
  deviceId: Device['id'];
  mediaType: RoomTrackStatsMediaType;
  source: string;
  trackId: string;
};

export type RoomTrackStatsHistoryEntry = {
  timestamp: number;
  bytesReceived: number;
  packetsReceived: number;
  packetsLost: number;
  framesDropped: number;
  framesDecoded: number;
};

export interface StatsManager {
  identity: RoomTrackStatsIdentity;
  refCount: number;
  interval: ReturnType<typeof setInterval> | null;
  deleteTimer: ReturnType<typeof setTimeout> | null;
  generation: number;
  sampleGeneration: number;
  history: RoomTrackStatsHistoryEntry[];
  stats: DeviceStats;
  listeners: Set<(stats: DeviceStats) => void>;
  track: Track;
  refreshInterval: number;
}

type StatsComputation = Awaited<ReturnType<typeof computeStats>>;

type InternalStatsManager = StatsManager & {
  disposed: boolean;
  lastFrameProgressAt: number | null;
  pendingPolls: Set<Promise<StatsComputation>>;
  pollGeneration: number | null;
};

export type RoomTrackStatsLease = {
  identity: RoomTrackStatsIdentity;
  release: () => void;
};

export type AcquireRoomTrackStatsOptions = {
  identity: RoomTrackStatsIdentity;
  track: Track;
  refreshInterval: number;
  listener?: (stats: DeviceStats) => void;
};

const managers = new Map<string, InternalStatsManager>();

export const createDefaultDeviceStats = (): DeviceStats => ({
  ready: false,
  bitrate: null,
  width: null,
  height: null,
  quality: null,
  packetLoss: null,
  framesDropped: null,
  framesPerSecond: null,
  codec: null
});

export const createRoomTrackStatsIdentity = (
  roomId: Room['id'],
  deviceId: Device['id'],
  mediaType: RoomTrackStatsMediaType,
  track: Track
): RoomTrackStatsIdentity => ({
  roomId,
  deviceId,
  mediaType,
  source: String(track.source ?? 'unknown'),
  trackId: `${track.sid || 'unknown'}:${track.mediaStreamTrack?.id || 'unknown'}`
});

const managerKey = ({
  roomId,
  deviceId,
  mediaType,
  source,
  trackId
}: RoomTrackStatsIdentity): string =>
  JSON.stringify([roomId, deviceId, mediaType, source, trackId]);

const resetManagerStats = (manager: StatsManager): void => {
  manager.history = [];
  manager.stats = createDefaultDeviceStats();
};

const notifyListeners = (manager: StatsManager): void => {
  for (const listener of manager.listeners) {
    listener(manager.stats);
  }
};

const stopManager = (manager: InternalStatsManager): void => {
  if (manager.interval !== null) {
    clearInterval(manager.interval);
    manager.interval = null;
  }

  if (manager.deleteTimer !== null) {
    clearTimeout(manager.deleteTimer);
    manager.deleteTimer = null;
  }
};

const disposeManager = (manager: InternalStatsManager): void => {
  manager.generation += 1;
  manager.disposed = true;
  stopManager(manager);
  resetManagerStats(manager);
  notifyListeners(manager);
  manager.listeners.clear();
  manager.refCount = 0;
};

const replaceTrack = (
  manager: InternalStatsManager,
  identity: RoomTrackStatsIdentity,
  track: Track
): void => {
  manager.generation += 1;
  manager.pollGeneration = null;
  manager.identity = identity;
  manager.track = track;
  manager.lastFrameProgressAt = null;
  resetManagerStats(manager);
  notifyListeners(manager);
};

export async function computeStats(
  track: Track | null,
  history: RoomTrackStatsHistoryEntry[]
): Promise<{ stats: DeviceStats; history: RoomTrackStatsHistoryEntry[] } | null> {
  if (!track) {
    return null;
  }

  const report = await track.getRTCStatsReport();

  if (!report) {
    return null;
  }

  let inbound: RTCInboundRtpStreamStats | undefined;
  report.forEach((value) => {
    if (value.type === 'inbound-rtp' && (value.kind === 'video' || value.kind === 'audio')) {
      inbound = value as RTCInboundRtpStreamStats;
    }
  });

  if (!inbound) {
    return null;
  }

  const current: RoomTrackStatsHistoryEntry = {
    timestamp: inbound.timestamp,
    bytesReceived: inbound.bytesReceived || 0,
    packetsReceived: inbound.packetsReceived || 0,
    framesDecoded: inbound.framesDecoded || 0,
    framesDropped: inbound.framesDropped || 0,
    packetsLost: inbound.packetsLost || 0
  };

  const nextHistory = history.filter((entry) => entry.timestamp >= current.timestamp - 61_000);
  nextHistory.push(current);

  const codecStats = inbound.codecId ? report.get(inbound.codecId) : undefined;

  const statistics: DeviceStats = {
    ready: true,
    bitrate: null,
    width: inbound.frameWidth || null,
    height: inbound.frameHeight || null,
    quality: null,
    packetLoss: null,
    framesDropped: null,
    framesPerSecond: inbound.framesPerSecond || null,
    codec: codecStats ? translateAudioCodec(codecStats.mimeType?.split('/')[1] ?? null) : null
  };

  if (nextHistory.length < 2) {
    return { stats: statistics, history: nextHistory };
  }

  const oneSecAgo = current.timestamp - 1_000;
  let prevSecIndex = -1;

  for (let i = nextHistory.length - 2; i >= 0; i--) {
    if ((nextHistory[i]?.timestamp ?? 0) <= oneSecAgo) {
      prevSecIndex = i;
      break;
    }
  }

  if (prevSecIndex === -1) {
    prevSecIndex = 0;
  }

  const prevSec = nextHistory[prevSecIndex];
  const deltaTimeSec = (current.timestamp - (prevSec?.timestamp ?? 0)) / 1_000;
  const deltaBytes = current.bytesReceived - (prevSec?.bytesReceived ?? 0);
  statistics.bitrate = deltaTimeSec > 0 ? (deltaBytes * 8) / deltaTimeSec : 0;

  const deltaReceived = current.packetsReceived - (prevSec?.packetsReceived ?? 0);
  const deltaLostSec = current.packetsLost - (prevSec?.packetsLost ?? 0);
  statistics.packetLoss = deltaReceived > 0 ? (deltaLostSec / deltaReceived) * 100 : 0;

  const deltaDecoded = current.framesDecoded - (prevSec?.framesDecoded ?? 0);
  const deltaDroppedSec = current.framesDropped - (prevSec?.framesDropped ?? 0);
  const totalAttemptedSec = deltaDecoded + deltaDroppedSec;
  const dropFraction = totalAttemptedSec > 0 ? deltaDroppedSec / totalAttemptedSec : 0;
  const lossFraction = (statistics.packetLoss ?? 0) / 100;
  statistics.quality = (1 - lossFraction) * (1 - dropFraction);

  const oneMinAgo = current.timestamp - 60_000;
  let prevMinIndex = -1;

  for (let i = nextHistory.length - 2; i >= 0; i--) {
    if ((nextHistory[i]?.timestamp ?? 0) <= oneMinAgo) {
      prevMinIndex = i;
      break;
    }
  }

  if (prevMinIndex === -1) {
    prevMinIndex = 0;
  }

  const prevMin = nextHistory[prevMinIndex];
  statistics.framesDropped = current.framesDropped - (prevMin?.framesDropped ?? 0);

  if (statistics.width === 0 || statistics.height === 0) {
    statistics.width = null;
    statistics.height = null;
  }

  if (statistics.bitrate !== null && statistics.bitrate <= 0) {
    statistics.bitrate = null;
  }

  return { stats: statistics, history: nextHistory };
}

const MIN_STATS_POLL_TIMEOUT_MS = 1_000;
const MAX_PENDING_STATS_POLLS = 2;

const pollManager = async (manager: InternalStatsManager): Promise<void> => {
  const generation = manager.generation;

  if (
    manager.disposed ||
    manager.pollGeneration === generation ||
    manager.pendingPolls.size >= MAX_PENDING_STATS_POLLS
  ) {
    return;
  }

  manager.pollGeneration = generation;
  const track = manager.track;
  const computation = computeStats(track, manager.history);
  manager.pendingPolls.add(computation);
  void computation.then(
    () => manager.pendingPolls.delete(computation),
    () => manager.pendingPolls.delete(computation)
  );
  let timeout: ReturnType<typeof setTimeout> | undefined;

  try {
    // Browser RTC stats promises are not cancellable; cap unresolved calls while allowing one retry.
    const result = await Promise.race([
      computation,
      new Promise<null>((resolve) => {
        timeout = setTimeout(
          () => resolve(null),
          Math.max(MIN_STATS_POLL_TIMEOUT_MS, manager.refreshInterval * 2)
        );
      })
    ]);

    // A replacement or teardown must not publish results from an older track generation.
    if (
      !result ||
      manager.disposed ||
      manager.generation !== generation ||
      manager.track !== track
    ) {
      return;
    }

    const previous = manager.history.at(-1);
    const current = result.history.at(-1);
    if (
      previous &&
      current &&
      current.timestamp > previous.timestamp &&
      current.framesDecoded > previous.framesDecoded
    ) {
      manager.lastFrameProgressAt = Date.now();
    }
    manager.stats = result.stats;
    manager.history = result.history;
    manager.sampleGeneration += 1;
    notifyListeners(manager);
  } catch {
    // A transient RTC stats failure should not stop later polling attempts.
  } finally {
    clearTimeout(timeout);
    if (manager.pollGeneration === generation) {
      manager.pollGeneration = null;
    }
  }
};

const startManager = (manager: InternalStatsManager): void => {
  if (manager.interval !== null) {
    return;
  }

  void pollManager(manager);
  manager.interval = setInterval(() => {
    void pollManager(manager);
  }, manager.refreshInterval);
};

export const acquireRoomTrackStats = ({
  identity,
  track,
  refreshInterval,
  listener
}: AcquireRoomTrackStatsOptions): RoomTrackStatsLease => {
  const key = managerKey(identity);
  let manager = managers.get(key);
  let trackReplaced = false;

  if (!manager) {
    manager = {
      identity,
      refCount: 0,
      interval: null,
      deleteTimer: null,
      generation: 0,
      sampleGeneration: 0,
      history: [],
      stats: createDefaultDeviceStats(),
      listeners: new Set(),
      track,
      refreshInterval,
      disposed: false,
      lastFrameProgressAt: null,
      pendingPolls: new Set(),
      pollGeneration: null
    };
    managers.set(key, manager);
  } else {
    if (manager.deleteTimer !== null) {
      clearTimeout(manager.deleteTimer);
      manager.deleteTimer = null;
    }

    if (manager.track !== track || manager.identity.trackId !== identity.trackId) {
      replaceTrack(manager, identity, track);
      trackReplaced = true;
    }
  }

  manager.refCount += 1;
  if (listener) {
    manager.listeners.add(listener);
    listener(manager.stats);
  }
  startManager(manager);
  if (trackReplaced) {
    void pollManager(manager);
  }

  let released = false;
  return {
    identity,
    release: () => {
      if (released) {
        return;
      }
      released = true;

      if (listener) {
        manager.listeners.delete(listener);
      }

      if (manager.disposed) {
        return;
      }

      manager.refCount = Math.max(0, manager.refCount - 1);
      if (manager.refCount > 0 || manager.deleteTimer !== null) {
        return;
      }

      // Deferred deletion lets React remounts reclaim the live poller without churn.
      manager.deleteTimer = setTimeout(() => {
        manager.deleteTimer = null;
        if (manager.refCount > 0 || managers.get(key) !== manager) {
          return;
        }

        managers.delete(key);
        disposeManager(manager);
      }, 0);
    }
  };
};

// Compare the actual track instance so reused SIDs cannot borrow another
// connection's progress. Wall-clock expiry also rejects a hung/stale stats poll.
export const areTrackFramesAdvancing = (track: Track): boolean => {
  for (const manager of managers.values()) {
    if (
      !manager.disposed &&
      manager.refCount > 0 &&
      manager.track === track &&
      manager.lastFrameProgressAt !== null &&
      Date.now() - manager.lastFrameProgressAt < 3000
    ) {
      return true;
    }
  }
  return false;
};

export const clearRoomTrackStats = (roomId: Room['id']): void => {
  for (const [key, manager] of managers) {
    if (manager.identity.roomId !== roomId) {
      continue;
    }

    managers.delete(key);
    disposeManager(manager);
  }
};

export const resetRoomTrackStatsForTests = (): void => {
  for (const manager of managers.values()) {
    disposeManager(manager);
  }
  managers.clear();
};

export const videoStatsManagersForRoom = (roomId: Room['id']): Map<Device['id'], StatsManager> => {
  const byDevice = new Map<Device['id'], StatsManager>();
  for (const manager of managers.values()) {
    if (
      !manager.disposed &&
      manager.identity.roomId === roomId &&
      manager.identity.mediaType === 'video'
    ) {
      byDevice.set(manager.identity.deviceId, manager);
    }
  }
  return byDevice;
};

const translateAudioCodec = (codec: string | null): string | undefined => {
  switch (codec) {
    case null:
      return undefined;
    case 'opus':
      return 'Opus';
    case 'aac':
      return 'AAC';
    default:
      return codec;
  }
};
